import { useState, type KeyboardEvent, type ReactNode } from 'react';
import './charts.css';

/** What a mark says on hover or focus: the value first, then when, then the details and a hint. */
export interface TipContent {
  /** The headline number, large: "2,008 pages". */
  value?: string | undefined;
  /** When or what: "Mon, Sep 21". */
  title: string;
  lines: readonly string[];
  /** A muted footer: what a click or Enter does. */
  hint?: string | undefined;
}

function TipBody({ tip }: { tip: TipContent }): ReactNode {
  return (
    <>
      {tip.value ? <strong className="pui-chart-tip__value">{tip.value}</strong> : null}
      <span className="pui-chart-tip__title">{tip.title}</span>
      {tip.lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
      {tip.hint ? <span className="pui-chart-tip__hint">↵ {tip.hint}</span> : null}
    </>
  );
}

/**
 * The tooltip over a chart frame at the mark's own position (pixels from the frame's top left). It
 * flips to the left of the mark past the middle so it never runs off the edge.
 */
export function ChartTip(props: { tip: TipContent; x: number; y: number; width: number }) {
  const { tip, x, y, width } = props;
  const flip = x > width * 0.55;
  return (
    <div
      className={['pui-chart-tip', flip && 'pui-chart-tip--flip'].filter(Boolean).join(' ')}
      style={{ left: `${x}px`, top: `${Math.max(0, y)}px` }}
    >
      <TipBody tip={tip} />
    </div>
  );
}

/**
 * On a phone a floating box covers the chart, so the same content sits in a strip above it and the
 * finger scrubs along the marks. Empty, it says how to use the chart.
 */
export function ChartReadout({ tip, empty }: { tip: TipContent | null; empty: string }): ReactNode {
  return (
    <div className="pui-chart-readout" aria-hidden="true">
      {tip ? <TipBody tip={{ ...tip, hint: undefined }} /> : <span>{empty}</span>}
    </div>
  );
}

const STEPS: Record<string, (columns: number) => number> = {
  ArrowLeft: () => -1,
  ArrowRight: () => 1,
  ArrowUp: (columns) => -columns,
  ArrowDown: (columns) => columns,
};

/** Where a key moves the active mark: an index, null to let go, undefined when the key means nothing. */
export function nextMark(
  key: string,
  active: number | null,
  count: number,
  columns: number,
): number | null | undefined {
  const step = STEPS[key];
  if (step) return Math.min(count - 1, Math.max(0, (active ?? count - 1) + step(columns)));
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (key === 'Escape') return null;
  return undefined;
}

export interface ActiveMarkOptions {
  /** Columns a row, for up and down on a grid. */
  columns?: number;
  /** Enter or Space on the active mark. */
  onPick?: ((index: number) => void) | undefined;
  /** Shift and an arrow: the active mark moves and the span from where Shift went down follows it. */
  onSpan?: ((from: number, to: number) => void) | undefined;
}

/** Enter or Space on an active mark goes to `onPick`; true when it did. */
function picked(
  event: KeyboardEvent,
  active: number | null,
  onPick: ActiveMarkOptions['onPick'],
): boolean {
  if ((event.key !== 'Enter' && event.key !== ' ') || !onPick || active === null) return false;
  event.preventDefault();
  onPick(active);
  return true;
}

/** Shift and an arrow: the span from the anchor to the new mark goes to `onSpan`; returns the anchor to keep. */
function stretch(
  onSpan: ActiveMarkOptions['onSpan'],
  from: number | null,
  next: number | null,
): number | null {
  if (!onSpan || next === null) return null;
  const start = from ?? next;
  onSpan(Math.min(start, next), Math.max(start, next));
  return start;
}

/**
 * The mark under the pointer or the keyboard. The chart is one tab stop; the arrows move between
 * marks (left and right, and up and down a grid), Home and End jump, Escape lets go, Enter or Space
 * hands the mark to `onPick`, and Shift with an arrow stretches a span for `onSpan`.
 */
export function useActiveMark(count: number, options: ActiveMarkOptions = {}) {
  const { columns = count, onPick, onSpan } = options;
  const [active, setActive] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const onKeyDown = (event: KeyboardEvent) => {
    if (picked(event, active, onPick)) return;
    const next = nextMark(event.key, active, count, columns);
    if (next === undefined) return;
    event.preventDefault();
    setActive(next);
    setAnchor(stretch(event.shiftKey ? onSpan : undefined, anchor ?? active, next));
  };
  return {
    active,
    setActive,
    focusProps: {
      tabIndex: 0,
      onKeyDown,
      onFocus: () => setActive((a) => a ?? count - 1),
      onBlur: () => setActive(null),
    },
  };
}
